/**
 * Contratos do modulo de funis (carrinho abandonado e expirados).
 *
 * Decisao central: a resposta vem FATIADA POR OFERTA (`slices`) e a tela soma
 * as fatias que o chip "Produto:" deixar passar. Assim o KPI em "Tudo", a linha
 * da tabela "Por oferta" e o KPI com uma oferta escolhida saem do MESMO
 * inteiro -- o total nunca discorda das partes. Percentuais nao viajam: sao
 * calculados na exibicao a partir de dois inteiros (`formatRatio`), e dinheiro
 * continua em centavos ate o formatador.
 *
 * Nada aqui existe no backend ainda (GUIA-PAINEL.md, secao 8). O vocabulario
 * espelha o do motor (`RecoveryTier`, `CampaignChannel`) para a troca de lado
 * do servidor ser so de transporte.
 */

import type { CompanyId, FunnelKind, Provider } from '../../types';

export type { FunnelKind };

/** Canal que executa o toque. E-mail nunca e toque proprio: sai junto do WhatsApp. */
export type TouchChannel = 'voice' | 'whatsapp';

/** A quem a conversao e creditada (ADR-001, ultimo contato). */
export type CreditChannel = 'voice' | 'whatsapp' | 'email' | 'seller';

export const CREDIT_CHANNELS: readonly CreditChannel[] = ['voice', 'whatsapp', 'email', 'seller'];

/**
 * Botao da mensagem de WhatsApp. `link` abre o checkout com a marca da regua
 * (pagou ali = recuperado pelo botao); `reply` cai na caixa do comercial e a
 * regua PARA na hora (vendedora fechou em ate 7 dias = recuperado pela vendedora).
 */
export interface TouchButton {
  readonly label: string;
  readonly kind: 'link' | 'reply';
}

export interface EmailCopy {
  readonly subject: string;
  readonly preheader: string;
  readonly body: string;
  readonly cta: string;
}

export interface TouchDefinition {
  readonly id: string;
  /** 1..n, na ordem em que a regua dispara. */
  readonly order: number;
  /** Minutos depois da ENTRADA no funil (abandono ou vencimento), nao do toque anterior. */
  readonly delayMinutes: number;
  readonly channel: TouchChannel;
  /** Corpo do WhatsApp ou roteiro resumido da ligacao da IA. */
  readonly body: string;
  /** So WhatsApp: 0 a 2 botoes. */
  readonly buttons: readonly TouchButton[];
  /** So voz. RF08: teto de 2 ligacoes por lead. */
  readonly maxAttempts: number;
  /** E-mail que sai junto do toque; `null` na ligacao. */
  readonly email: EmailCopy | null;
  readonly enabled: boolean;
  readonly isLast: boolean;
}

export interface VoiceMetrics {
  /** Ligacoes discadas (inclui a 2a tentativa). */
  readonly calls: number;
  readonly answered: number;
  readonly voicemail: number;
  readonly noAnswer: number;
  readonly failed: number;
  /** Pagaram durante ou logo apos a ligacao. */
  readonly paidAfterCall: number;
}

export interface EmailMetrics {
  readonly sent: number;
  /** Enviados como reserva porque o WhatsApp nao entregou (subconjunto de `sent`). */
  readonly fallback: number;
  readonly skipped: number;
  readonly opened: number;
  readonly clicked: number;
  /** Pagaram pelo link do e-mail. */
  readonly paid: number;
}

/**
 * Resultado de um toque na janela. Cada pessoa que chegou ao toque termina em
 * EXATAMENTE um destino: pagou, respondeu, descadastrou ou seguiu (no ultimo
 * toque, "chegou ao fim sem voltar"). `queued`/`waitingNext` sao um RETRATO DE
 * HOJE e ficam fora dessa soma.
 */
export interface TouchMetrics {
  readonly touchId: string;
  /** `false` quando o funil ainda nao existe ha tempo suficiente para medir este marco. */
  readonly measurable: boolean;
  /** Pessoas esperando ESTE toque agora (na fila hoje) -- retrato, nao periodo. */
  readonly queued: number;
  /** Pessoas que receberam o toque na janela (ou foram ligadas). */
  readonly reached: number;
  readonly delivered: number;
  readonly opened: number;
  /** Pessoas, nao cliques: quem abriu o link tres vezes conta uma. */
  readonly clicked: number;
  readonly paid: number;
  readonly paidByButton: number;
  readonly paidAmountCents: number;
  readonly replied: number;
  readonly closedBySeller: number;
  readonly closedBySellerAmountCents: number;
  readonly unsubscribed: number;
  /** Ja passaram deste toque e esperam o proximo. */
  readonly waitingNext: number;
  /** Seguiram para o proximo toque dentro da janela. */
  readonly movedOn: number;
  /** So no ultimo toque. */
  readonly endedWithoutReturn: number;
  readonly failed: number;
  /** Valor de quem esta na fila deste marco hoje. */
  readonly stuckAmountCents: number;
  readonly voice: VoiceMetrics | null;
  readonly email: EmailMetrics | null;
}

export interface FunnelDailyPoint {
  /** YYYY-MM-DD */
  readonly day: string;
  readonly entered: number;
  readonly recovered: number;
  readonly recoveredAmountCents: number;
}

/**
 * Os mesmos inteiros-base dos quatro KPIs, medidos na janela IMEDIATAMENTE
 * ANTERIOR (mesmo tamanho, terminando onde a atual comeca). `null` quando a
 * janela e "Desde o inicio": nao existe periodo anterior para comparar, e a
 * tela diz isso em vez de mostrar "0%".
 */
export interface PreviousPeriodMetrics {
  readonly checkouts: number;
  readonly paidOnTime: number;
  readonly entered: number;
  readonly recovered: number;
  readonly recoveredAmountCents: number;
  readonly commissionCents: number;
}

export interface OfferMetrics {
  /** Carrinho: checkouts iniciados. Expirados: assinaturas que chegaram ao fim (= entered). */
  readonly checkouts: number;
  /** Carrinho: pagaram dentro dos 30 min (nunca entram no funil). Expirados: 0. */
  readonly paidOnTime: number;
  /** Abandonaram / venceram. E a base de toda taxa. */
  readonly entered: number;
  /** Expirados: vencem ainda hoje (subconjunto de `entered`). */
  readonly dueToday: number;
  /** Expirados: pagaram de novo antes do primeiro toque; nao entram na taxa. */
  readonly returnedAlone: number;
  readonly returnedAloneAmountCents: number;
  /** Recuperados pelo funil, de quem entrou no periodo. */
  readonly recovered: number;
  readonly recoveredAmountCents: number;
  readonly recoveredByChannel: Readonly<Record<CreditChannel, number>>;
  readonly recoveredAmountByChannelCents: Readonly<Record<CreditChannel, number>>;
  /** Vendas no periodo de entradas ANTERIORES ao periodo (janela de 7 dias cruza a borda). */
  readonly carryOverRecovered: number;
  readonly carryOverAmountCents: number;
  /** Sobre `recoveredAmountCents + carryOverAmountCents`, com o fee congelado. */
  readonly commissionCents: number;
  /** Responderam e a vendedora ainda nao fechou. */
  readonly withSeller: number;
  readonly unsubscribed: number;
  /** Ainda parados / ainda fora: entraram e nao tiveram desfecho (nem pagaram, nem responderam, nem sairam). */
  readonly stillOut: number;
  readonly stuckAmountCents: number;
  readonly touches: readonly TouchMetrics[];
  readonly daily: readonly FunnelDailyPoint[];
  /** Janela anterior, para o delta dos KPIs. `null` sem periodo anterior. */
  readonly previous: PreviousPeriodMetrics | null;
}

export interface FunnelOfferRef {
  readonly id: string;
  readonly name: string;
  readonly priceCents: number;
  readonly recurring: boolean;
  readonly provider: Provider;
}

export interface FunnelOfferSlice {
  readonly offer: FunnelOfferRef;
  readonly metrics: OfferMetrics;
}

export type FunnelLeadStatus =
  | 'na_regua'
  | 'pagou'
  | 'com_comercial'
  | 'vendedora_fechou'
  | 'descadastrou'
  | 'sem_voltar'
  | 'voltou_sozinho';

export interface FunnelLead {
  readonly id: string;
  readonly companyId: CompanyId;
  readonly name: string | null;
  /** E.164; a tela SEMPRE passa por `maskPhoneDisplay`. */
  readonly phoneE164: string;
  readonly offerId: string;
  readonly offerName: string;
  readonly amountCents: number;
  readonly provider: Provider;
  /** Abandonou em / venceu em. */
  readonly enteredAt: string;
  /** Ultimo toque recebido (`order`); 0 = ainda nenhum. */
  readonly lastTouchOrder: number;
  readonly status: FunnelLeadStatus;
  readonly recoveredAt: string | null;
  readonly recoveredVia: CreditChannel | null;
}

export interface FunnelOverview {
  readonly kind: FunnelKind;
  readonly companyId: CompanyId;
  readonly companyName: string;
  readonly feeBps: number;
  /** Nome de quem responde pelo funil na empresa. */
  readonly responsible: string;
  /** Disparo automatico ligado no servidor. */
  readonly active: boolean;
  /** Desde quando o funil roda; decide o que ja da para medir. */
  readonly funnelSinceAt: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly touches: readonly TouchDefinition[];
  readonly slices: readonly FunnelOfferSlice[];
  readonly leads: readonly FunnelLead[];
}

/** Um degrau de "O caminho do carrinho" / "O caminho de quem venceu". */
export interface FunnelStep {
  readonly key: 'entered' | 'paid' | 'out';
  readonly eyebrow: string;
  readonly value: number;
  readonly label: string;
  readonly detail: string;
  readonly tone: 'accent' | 'good' | 'critical';
  /** Numerador/denominador da proporcao em relacao a quem entrou; `null` no primeiro degrau. */
  readonly ratio: { readonly part: number; readonly whole: number } | null;
}

/**
 * Sinal automatico de uma oferta na tabela. `oportunidade` = muita gente (e
 * dinheiro) parada numa oferta que historicamente volta bem: vale um toque a
 * mais ou uma condicao especial. `atencao` = abandona muito e quase nao volta:
 * o problema esta no checkout ou na copy, nao na regua.
 */
export type SinalDaOferta = 'oportunidade' | 'atencao';

/** Linha da tabela "Por oferta" / "Por plano", derivada de uma fatia. */
export interface OfferRow {
  readonly offer: FunnelOfferRef;
  readonly checkouts: number;
  readonly paidOnTime: number;
  readonly entered: number;
  readonly recovered: number;
  readonly recoveredAmountCents: number;
  readonly commissionCents: number;
  readonly returnedAlone: number;
  readonly withSeller: number;
  readonly stillOut: number;
  readonly stuckAmountCents: number;
  readonly sinal: SinalDaOferta | null;
}

/** Melhor e pior toque da regua na janela, por conversao (pagou + vendedora fechou / alcancados). */
export interface DestaquesDaRegua {
  readonly melhor: string | null;
  readonly pior: string | null;
}

/** Variacao de um KPI em relacao ao periodo anterior. Inteiros; a divisao so acontece na exibicao. */
export interface Delta {
  readonly atual: number;
  readonly anterior: number;
  /** Diferenca absoluta (atual - anterior). */
  readonly diferenca: number;
  /** Variacao relativa em bps; `null` quando o anterior e zero. */
  readonly bps: number | null;
}

/** O que o insight sugere fazer: um clique leva ate o lugar certo da propria pagina. */
export type AcaoDoInsight =
  | { readonly kind: 'oferta'; readonly offerId: string }
  | { readonly kind: 'config'; readonly touchId: string | null }
  | { readonly kind: 'leads'; readonly status: FunnelLeadStatus | null };

export type TomDoInsight = 'oportunidade' | 'alerta' | 'projecao' | 'bom';

/** Um cartao de "O que fazer agora", derivado SO dos numeros da janela. */
export interface Insight {
  readonly key: string;
  readonly tom: TomDoInsight;
  readonly titulo: string;
  readonly texto: string;
  readonly acao: AcaoDoInsight | null;
  readonly rotuloAcao: string | null;
}

/** Resumo da faixa "Disparo automatico". */
export interface DispatchSummary {
  readonly entered: number;
  readonly whatsappSent: number;
  readonly calls: number;
  readonly emailsSent: number;
  readonly emailsFallback: number;
  readonly emailsOpened: number;
  readonly clicked: number;
  readonly queuedNow: number;
  readonly leftBecausePaid: number;
  readonly leftBecauseReplied: number;
  readonly unsubscribed: number;
}
